import { capitalize, omit } from 'lodash-es'
import { type ChangeEvent, useEffect, useRef, useState } from 'react'

import {
  addressFuncs,
  isDirtyForm,
  useCurrencyOptions,
  validateEmailInput,
} from '@fv/client-core'
import type {
  ClientAddressLookupResult,
  SelectFieldOption,
  ShipmentLocation,
} from '@fv/client-types'
import {
  type Currency,
  type CustomsBrokerType,
  customsBrokerTypes,
  type Workflow,
} from '@fv/models'

import { AddressInput } from '@/components/inputs/AddressInput'
import { ContactPhoneInput } from '@/components/inputs/ContactPhoneInput'
import {
  InputAdornment,
  InputGroup,
  InputGroupWrapper,
} from '@/components/inputs/InputGroup'
import { SelectField } from '@/components/inputs/SelectField'
import type { FormSectionProps } from '@/components/inputs/types'
import { Loading } from '@/components/Loading'

import { AddressBookButton } from '../addresses/AddressBookButton'
import { customsBrokerMapper } from './brokerFuncs'
import type {
  FormCustomsBroker,
  ShipmentCustomsBrokerFormModel,
} from './brokerTypes'
import { useCustomsBrokerByLocation } from './useBrokers'

export type LoadLocationLookup = Pick<
  ShipmentLocation,
  'addressBookId' | 'postalCode'
>

type Props = FormSectionProps & {
  loadLocation: LoadLocationLookup
  locations?: Array<{ country?: string }>
  autofocus?: boolean
  nameLabel?: string
  required?: boolean
  setValues: (values: Partial<ShipmentCustomsBrokerFormModel>) => void
  values: ShipmentCustomsBrokerFormModel
  requireValue?: boolean
  workflow: Workflow
}

export const CustomsBrokerForm = ({
  autofocus = false,
  disabled = false,
  loadLocation,
  locations,
  nameLabel = 'Customs broker company name',
  required = true,
  setValues,
  values,
  requireValue,
  workflow,
}: Props) => {
  const currencyOptions = useCurrencyOptions(locations)
  const addressRef = useRef<HTMLInputElement>(null)
  const enabled = !!(loadLocation || required)
  const {
    getInputProps,
    setFormValues,
    isLoading: formLoading,
  } = useForm({
    disabled,
    loadLocation,
    setValues,
    values,
  })

  if (!enabled) return null

  const typeOptions: SelectFieldOption[] = [
    {
      text: '',
      value: '',
    },
    ...customsBrokerTypes.map(c => ({
      text: capitalize(c),
      value: c,
    })),
  ]

  const handleAddressSelect = (address: ClientAddressLookupResult | null) => {
    setFormValues({
      companyName: address?.company || '',
      address: address?.address || '',
      address2: address?.address2 || '',
      city: address?.city || '',
      postalCode: address?.postalCode || '',
      state: address?.state || '',
      country: address?.country || 'us',
      email: address?.contactEmail || '',
      phone: address?.contactPhone || '',
      phoneExt: address?.contactPhoneExt || '',
      addressBookId: address?._id || '',
    })
  }

  if (formLoading) {
    return <Loading />
  }

  return (
    <>
      <InputGroupWrapper
        label={nameLabel}
        name={getInputName('companyName')}
        required={required}
      >
        <AddressBookButton
          config={{ context: workflow, type: 'customs-broker' }}
          setAddress={handleAddressSelect}
        />
        <AddressInput
          autoFocus={autofocus}
          context={
            workflow === 'truckload'
              ? 'tl-customs-broker'
              : 'ltl-customs-broker'
          }
          addressType="customs-broker"
          includeAddressBook
          includeAtlas={false}
          location={{
            ...values,
            company: values.companyName,
          }}
          allowCustomInput
          onInputChange={companyName =>
            setFormValues({ ...values, companyName })
          }
          formatAddress={x => x.company}
          name="customsBroker_companyName"
          onLocationChange={handleAddressSelect}
          placeholder="Company name"
          required={required}
        />
      </InputGroupWrapper>

      <InputGroup
        inputProps={getInputProps('address', {
          ref: addressRef,
        })}
        inputType="text"
        label="Address"
      />

      <InputGroup
        inputProps={getInputProps('address2')}
        inputType="text"
        label="Address 2"
      />

      <InputGroupWrapper
        label="City, State, Zip, Country"
        name={getInputName('cityStateZipCountry')}
      >
        <AddressInput
          context={workflow}
          formatAddress={addressFuncs.cityStateZip}
          location={{
            ...values,
            country: values.country || 'us',
          }}
          name={getInputName('cityStateZipCountry')}
          onLocationChange={x => {
            setFormValues({
              adminArea3: x?.adminArea3,
              city: x?.city || '',
              country: x?.country || 'us',
              postalCode: x?.postalCode || '',
              state: x?.state || '',
            })
          }}
          readOnly={disabled}
        />
      </InputGroupWrapper>

      <InputGroupWrapper label="Type" name={getInputName('type')}>
        <SelectField
          name={getInputName('type')}
          options={typeOptions}
          value={values.type ?? ''}
          onChange={e =>
            setFormValues({ type: e.target.value as CustomsBrokerType })
          }
        />
      </InputGroupWrapper>

      <ContactPhoneInput
        className="basis-3/12"
        disabled={disabled}
        label="Phone"
        name={getInputName('phone')}
        onChange={v => {
          setFormValues({
            phone: v.contactPhone,
            phoneExt: v.contactPhoneExt,
          })
        }}
        required={required}
        values={{
          contactPhone: values.phone,
          contactPhoneExt: values.phoneExt,
        }}
      />

      <InputGroup
        inputProps={getInputProps('email')}
        inputType="text"
        label="Email"
      />

      <InputGroup
        inputProps={getInputProps('value', {
          isPrice: true,
          min: 1,
        })}
        inputType="amount"
        label="Declared value"
        required={required && requireValue}
        startContent={
          <InputAdornment
            icon="dollar-sign"
            iconClass="text-fv-gray-dark"
            position="start"
          />
        }
        endContent={
          workflow === 'truckload' && (
            <SelectField
              className="w-auto"
              name={getInputName('valueCurrency')}
              value={values.valueCurrency || 'usd'}
              onChange={e =>
                setFormValues({
                  valueCurrency: e.target.value as Currency,
                })
              }
              options={currencyOptions}
            />
          )
        }
      />
    </>
  )
}

function getInputName(name: string) {
  return `customsBroker_${name}`
}

function useForm(config: {
  loadLocation: Pick<ShipmentLocation, 'addressBookId' | 'postalCode'>
  disabled: boolean
  setValues: (values: Partial<FormCustomsBroker>) => void
  values: FormCustomsBroker
}) {
  const [loading, setLoading] = useState(true)
  const { disabled, loadLocation, setValues, values } = config
  const [dirty, setDirty] = useState(() => isDirtyForm(omit(values, 'country')))
  const enableBrokerLookup = !!loadLocation && !dirty

  const defaultBrokerQuery = useCustomsBrokerByLocation(
    loadLocation,
    enableBrokerLookup,
  )

  useEffect(() => {
    if (defaultBrokerQuery.isLoading && enableBrokerLookup) return
    if (!loadLocation || dirty || !defaultBrokerQuery.data) {
      setLoading(false)
      return
    }

    const defaultBroker = customsBrokerMapper.dtoToForm(defaultBrokerQuery.data)

    setDirty(true)
    setValues(defaultBroker)
    setLoading(false)
  }, [
    defaultBrokerQuery.isLoading,
    defaultBrokerQuery.data,
    dirty,
    loadLocation,
    setValues,
    enableBrokerLookup,
  ])

  function setFormValues(values: Partial<FormCustomsBroker>) {
    setDirty(true)
    setValues(values)
  }

  return {
    isLoading: loading,
    getInputProps: (
      name: 'address' | 'address2' | 'email' | 'value',
      inputProps?: object,
    ) => ({
      disabled,
      name: getInputName(name),
      onChange: (e: ChangeEvent<HTMLInputElement>) => {
        if (name === 'email') {
          validateEmailInput(e.target)
        }

        setDirty(true)
        setValues({ [name]: e.target.value })
      },
      value: values[name] ?? '',
      ...inputProps,
    }),
    setFormValues,
  }
}
